import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getIssueStatus,
  createIssueStatus,
  updateIssueStatus,
} from '../../../Api/request/issueStatus';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { issueStatusTableConfig } from './issueStatusTableConfig';
import './issueStatus.scss';

const EMPTY_FORM = { name: '' };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return '';
};

const normalizeIssueStatusLists = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.issueStatus || [];

  return rows.map((issueStatus) => normalizeIssueStatus(issueStatus));
};

const normalizeIssueStatus = (issueStatus) => ({
  id: issueStatus.issueStatusId,
  name: issueStatus.issueStatusName || '',
  isActive: Boolean(issueStatus.isActive),
});

const IssueStatusForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="issue-status-modal-backdrop">
    <form className="issue-status-modal" onSubmit={onSubmit}>
      <div className="issue-status-modal__header">
        {mode === 'edit' ? 'Edit Issue Status' : 'Add Issue Status'}
      </div>
      <div className="issue-status-modal__body">
        <label htmlFor="department-name" className={error ? 'is-error' : ''}>
           Issue Status<span className="required-asterisk">*</span>
        </label>
        <input
          id="department-name"
          name="name"
          type="text"
          placeholder="Enter Issue Status"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
      </div>
      <div className="issue-status-modal__footer">
        <button type="button" className="issue-status-modal__btn issue-status-modal__btn--ghost"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="issue-status-modal__btn issue-status-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const IssueStatus = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_ISSUE_STATUS];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [ issueStatuses, setIssueStatuses ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleIssueStatus, setToggleIssueStatus] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadIssueStatusList = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getIssueStatus(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setIssueStatuses(normalizeIssueStatusLists(response));
  }, [hasAccess]);

  useEffect(() => {
    loadIssueStatusList();
  }, [loadIssueStatusList]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name });
    setHasError(false);
  }, [hasAccess]);

  const selectedIssueStatus = issueStatuses.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && (form.name.trim() === selectedIssueStatus?.name.trim()
    ));

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a issue status name.');
      return;
    }
    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateIssueStatus(dialog.id, name, selectedIssueStatus?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createIssueStatus(name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Issue Status updated successfully.'
      : 'Issue Status added successfully.');
    const savedIssueStatus = normalizeIssueStatus(response?.data?.issueStatus || response?.data);
    setIssueStatuses((currentIssueStatuses) => (
      dialog.mode === 'edit'
        ? currentIssueStatuses.map((iStatus) => (
          iStatus.id === savedIssueStatus.id ? savedIssueStatus : iStatus
        ))
        : [...currentIssueStatuses, savedIssueStatus]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, hasAccess, selectedIssueStatus]);

  const handleToggle = useCallback((iStatus) => {
    if (!hasAccess('update')) return;
    setToggleIssueStatus(iStatus);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleIssueStatus(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleIssueStatus || !hasAccess('update')) return;

    const nextStatus = !toggleIssueStatus.isActive;
    const response = await UseAxios(
      updateIssueStatus(
        toggleIssueStatus.id, toggleIssueStatus.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setIssueStatuses((currentIssueStatuses) => currentIssueStatuses.map((issueStatus) => (
      issueStatus.id === toggleIssueStatus.id
        ? { ...issueStatus, isActive: nextStatus } : issueStatus
    )));
    toast.success(`${toggleIssueStatus.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleIssueStatus]);

  const tableData = useMemo(() => issueStatuses.map((issueStatusData, index) => ({
    ...issueStatusData,
    slNo: index + 1,
    issueStatus: issueStatusData.name,
    status: issueStatusData.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="issue-status-list-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={issueStatusData.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(issueStatusData)}
        />
        <button
          type="button"
          className="issue-status-list-action-btn"
          title="Edit Issue Status"
          aria-label={`Edit ${issueStatusData.issueStatus}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(issueStatusData)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, issueStatuses]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Issue Status',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: issueStatusTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: issueStatusTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div style={{marginTop: '30px'}}>You do not have permission to view issue status.</div>;
  }

  const onChangeStatusForm = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setHasError(false);
  };

  return (
    <div className="issue-status-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <IssueStatusForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={onChangeStatusForm}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleIssueStatus)}
        title={`${toggleIssueStatus?.isActive ? 'Disable' : 'Enable'} Issue Status`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleIssueStatus?.isActive
                ? 'disable' : 'enable'} this issue status?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleIssueStatus?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default IssueStatus;
